import type { ReactNode } from 'react'
import { AlertDialog } from 'radix-ui'
import { Button } from './Button'

export interface ConfirmDialogProps {
  open: boolean
  title?: string
  message?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  destructive?: boolean
  onConfirm: () => void
  onCancel: () => void
  busy?: boolean
}

/**
 * Confirmation on Radix AlertDialog.
 *
 * It used to be a plain `fixed z-50` div rendered in place. Opened from inside a Modal
 * (a Radix Dialog portalled to <body> at z-[60]/z-[70], which also makes everything
 * outside itself inert), it sat BEHIND the modal and could not be clicked — so every
 * "confirm inside a dialog" flow silently dead-ended. Now it portals to <body> above any
 * dialog (overlay z-[80], content z-[90]), joins Radix's layer stack so Escape and focus
 * return go to the right layer, and traps focus with the safe action focused first.
 * The props are unchanged, so every call site upgrades without edits.
 */
export function ConfirmDialog({
  open, title = 'Are you sure?', message, confirmLabel = 'Confirm',
  cancelLabel = 'Cancel', destructive, onConfirm, onCancel, busy,
}: ConfirmDialogProps) {
  return (
    <AlertDialog.Root open={open} onOpenChange={(o) => { if (!o && !busy) onCancel() }}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 z-[80] bg-overlay/40 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <AlertDialog.Content
          data-confirm-dialog=""
          className="fixed left-1/2 top-1/2 z-[90] w-[calc(100vw-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-overlay border border-border bg-card p-5 shadow-e4 focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[0.98]"
          {...(message ? {} : { 'aria-describedby': undefined })}
        >
          <AlertDialog.Title className="text-base font-semibold text-foreground">{title}</AlertDialog.Title>
          {message && (
            <AlertDialog.Description asChild>
              <div className="mt-2 text-sm text-content-secondary">{message}</div>
            </AlertDialog.Description>
          )}
          <div className="mt-5 flex flex-wrap justify-end gap-2">
            <AlertDialog.Cancel asChild>
              <Button variant="outline" disabled={busy}>{cancelLabel}</Button>
            </AlertDialog.Cancel>
            {/* Not AlertDialog.Action: that closes the dialog before an async confirm has
                finished. The caller closes it (open=false) once the action has landed. */}
            <Button variant={destructive ? 'danger' : 'primary'} loading={busy} onClick={onConfirm}>
              {confirmLabel}
            </Button>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  )
}
